import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { doc, onSnapshot } from 'firebase/firestore'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { useLetterConfig } from '@/hooks/use-letter-config'
import { callableErrorMessage } from '@/lib/callable-errors'
import { sendLetterForExistingBiddle } from '@/lib/callables'
import { db } from '@/lib/firebase'
import { cn } from '@/lib/utils'

function letterStatusLabel(status: string): string {
  switch (status) {
    case 'created':
    case 'queued':
      return 'Letter queued'
    case 'ready':
      return 'Letter ready'
    case 'printing':
      return 'Letter printing'
    case 'processed_for_delivery':
      return 'Letter handed to post'
    case 'in_transit':
      return 'Letter in transit'
    case 'delivered':
      return 'Letter delivered'
    case 'returned_to_sender':
      return 'Letter returned'
    case 'cancelled':
      return 'Letter cancelled'
    case 'failed':
    case 'queue-failed':
    case 'rejected':
      return 'Letter failed'
    default:
      return 'Letter sending'
  }
}

/**
 * Buyer-only: send a physical letter for an existing active Biddle,
 * or show live status from `letterSends/{biddleId}` (iOS parity).
 */
export function SendLetterPanel({
  biddleId,
  propertyId,
  propertyAddress,
  eligible,
}: {
  biddleId: string
  propertyId: string
  propertyAddress: string
  /** Buyer + active + not deleted / not yet in chat — parent already computed. */
  eligible: boolean
}) {
  const { user, profile } = useAuth()
  const { config, loading: configLoading } = useLetterConfig()
  const [sendStatus, setSendStatus] = useState<string | null>(null)
  const [hasSendDoc, setHasSendDoc] = useState(false)
  const [hasPropertyLetter, setHasPropertyLetter] = useState(false)
  const [listenersReady, setListenersReady] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justSent, setJustSent] = useState(false)

  const credits = profile?.letterCredits ?? 0
  const whitelisted =
    config.whitelistUids.length === 0 ||
    (user?.uid != null && config.whitelistUids.includes(user.uid))
  const featureOn = config.enabled && whitelisted

  useEffect(() => {
    if (!user || !biddleId || !propertyId) return

    let sendLoaded = false
    let propertyLoaded = false
    const markReady = () => {
      if (sendLoaded && propertyLoaded) setListenersReady(true)
    }

    const unsubSend = onSnapshot(
      doc(db, 'letterSends', biddleId),
      (snap) => {
        sendLoaded = true
        setHasSendDoc(snap.exists())
        const status = snap.exists() ? String(snap.data()?.status ?? 'created') : null
        setSendStatus(status)
        markReady()
      },
      () => {
        sendLoaded = true
        setHasSendDoc(false)
        setSendStatus(null)
        markReady()
      },
    )

    const unsubProp = onSnapshot(
      doc(db, 'users', user.uid, 'lettersByProperty', propertyId),
      (snap) => {
        propertyLoaded = true
        setHasPropertyLetter(snap.exists())
        markReady()
      },
      () => {
        propertyLoaded = true
        setHasPropertyLetter(false)
        markReady()
      },
    )

    return () => {
      unsubSend()
      unsubProp()
      setListenersReady(false)
    }
  }, [user, biddleId, propertyId])

  async function onSend() {
    setBusy(true)
    setError(null)
    try {
      await sendLetterForExistingBiddle(biddleId)
      setJustSent(true)
      setConfirming(false)
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  if (!eligible) return null
  if (configLoading || !listenersReady) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted">
        <Spinner className="h-4 w-4" />
        Checking letter status…
      </div>
    )
  }

  if (!featureOn) return null

  if (hasSendDoc && sendStatus) {
    return (
      <div className="rounded-xl border border-border bg-surface px-4 py-3">
        <p className="text-sm font-semibold text-navy">{letterStatusLabel(sendStatus)}</p>
        <p className="mt-1 text-xs text-muted">Posted to {propertyAddress}</p>
      </div>
    )
  }

  if (justSent || hasPropertyLetter) {
    return (
      <div className="rounded-xl border border-border bg-surface px-4 py-3">
        <p className="text-sm font-semibold text-navy">
          {justSent ? 'Letter queued' : 'Letter already sent to this property'}
        </p>
        <p className="mt-1 text-xs text-muted">Posted to {propertyAddress}</p>
      </div>
    )
  }

  if (!confirming) {
    return (
      <Button type="button" variant="outline" onClick={() => setConfirming(true)}>
        Send letter
      </Button>
    )
  }

  if (credits < 1) {
    return (
      <div className="space-y-3 rounded-xl border border-border px-4 py-4">
        <div>
          <p className="font-semibold text-navy">Send a physical letter</p>
          <p className="mt-1 text-sm text-muted">
            You need 1 letter credit to post to {propertyAddress}.
          </p>
        </div>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="primary">
            <Link to="/letters">Buy letter pack</Link>
          </Button>
          <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-xl border border-border px-4 py-4">
      <div>
        <p className="font-semibold text-navy">Send a physical letter</p>
        <p className="mt-1 text-sm text-muted">
          Posting to {propertyAddress}. Uses 1 of your {credits} credit{credits === 1 ? '' : 's'}.
        </p>
      </div>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="primary" disabled={busy} onClick={() => void onSend()}>
          {busy ? <Spinner className="border-white border-t-transparent" /> : null}
          Send letter
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          className={cn(busy && 'opacity-50')}
          onClick={() => setConfirming(false)}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
